import kyWithSentry from './helper/kyWithSentry';

import getDiscoveryKPIUrl from 'url-constructors/Shazam/getDiscoveryKPIUrl';
import getDiscoveryKPICities from 'url-constructors/Shazam/getDiscoveryKPICities';
import getDiscoveryKPICountries from 'url-constructors/Shazam/getDiscoveryKPICountries';
import getByUrl from './helper/getByUrl';

interface IOptions {
  entityIds: string[];
  entityType: 'track' | 'artist';
  limit: number;
  sortOrder: string;
}

interface ICompareCitiesUrls {
  countryIds: string[];
  options: IOptions;
  sortBy: string;
}

const getCompareCitiesUrls = ({
  countryIds,
  options,
  sortBy,
}: ICompareCitiesUrls): string[] => {
  return countryIds.reduce(
    (prev: any, country: string): any => [
      ...prev,
      getDiscoveryKPICities({
        ...options,
        sortBy,
        countryIds: country,
      }),
    ],
    []
  );
};

const getCompareCitiesList = async (
  countryIds: string[],
  options: IOptions,
  sortBy: string
): Promise<any> => {
  const compareCitiesUrls = getCompareCitiesUrls({
    countryIds,
    options,
    sortBy,
  });
  const promisesArray = compareCitiesUrls.map(async (item: string) => {
    return (await kyWithSentry.get(item).json()) as any;
  });
  return Promise.all(promisesArray);
};

const getDiscoveryCountriesData = (
  options: any,
  sortBy: string,
  region: string,
  countryIds: string,
  customRegion: string | string[]
): any =>
  kyWithSentry
    .get(
      getDiscoveryKPICountries({
        ...options,
        sortBy,
        region,
        countryIds,
        customRegion,
      })
    )
    .json();

const getDiscoveryCities = (
  options: any,
  sortBy: string,
  countryIds: any,
  region: string,
  customRegion: string | string[]
): any =>
  kyWithSentry
    .get(
      getDiscoveryKPICities({
        ...options,
        sortBy,
        countryIds,
        region,
        customRegion,
      })
    )
    .json();

const flatArray = (array: any): any =>
  array.reduce((prev: any, item: any) => [...prev, ...item], []);

const discoveryKPIInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  const {
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters,
    customRegion,
  } = interceptionData;
  const options = {
    entityIds,
    entityType,
    limit: 5,
    sortOrder: 'desc',
  };

  const url = getDiscoveryKPIUrl({
    entityIds,
    entityType,
    countryIds: compareMode === 'country' ? countryIds : filters.country,
    region: filters?.region,
    customRegion,
    compareMode,
  });

  const discoveryKPIDataFetch = async (): Promise<any> => getByUrl<any>(url);

  const discoveryTWTDCountriesDataFetch = async (): Promise<any> =>
    getDiscoveryCountriesData(
      options,
      'countThisWeekToDate',
      filters.region,
      filters.country,
      customRegion
    );

  const discoveryLWCountriesDataFetch = async (): Promise<any> =>
    getDiscoveryCountriesData(
      options,
      'countLastWeek',
      filters.region,
      filters.country,
      customRegion
    );

  const discoveryCitiesTWTDFetch = async (): Promise<any> =>
    compareMode === 'country' && Array.isArray(countryIds)
      ? flatArray(
          await getCompareCitiesList(countryIds, options, 'countThisWeekToDate')
        )
      : await getDiscoveryCities(
          options,
          'countThisWeekToDate',
          filters.country,
          filters.region,
          customRegion
        );

  const discoveryCitiesLWFetch = async (): Promise<any> =>
    compareMode === 'country' && Array.isArray(countryIds)
      ? flatArray(
          await getCompareCitiesList(countryIds, options, 'countLastWeek')
        )
      : await getDiscoveryCities(
          options,
          'countLastWeek',
          filters.country,
          filters.region,
          customRegion
        );

  try {
    const promises: Promise<any>[] = [
      discoveryKPIDataFetch(),
      discoveryTWTDCountriesDataFetch(),
      discoveryLWCountriesDataFetch(),
      discoveryCitiesTWTDFetch(),
      discoveryCitiesLWFetch(),
    ];

    const [
      discoveryKPIData,
      discoveryTWTDCountriesData,
      discoveryLWCountriesData,
      discoveryCitiesTWTD,
      discoveryCitiesLW,
    ] = await Promise.all(promises);

    return new Response(
      JSON.stringify({
        discoveryKPIData,
        discoveryTWTDCountriesData,
        discoveryLWCountriesData,
        discoveryCitiesTWTD,
        discoveryCitiesLW,
        url,
      })
    );
  } catch (error) {
    const errorInfo = await error.response.json();
    return new Response(
      JSON.stringify({
        reason: {
          error: errorInfo.message,
          errorInfo,
        },
      })
    );
  }
};

export default discoveryKPIInterceptor;
